/**
 * 版本历史对话框（Wave 4 R3 / T4.7 集成）。
 *
 * 生命周期"版本管理"入口（ARCHITECTURE_DESIGN.md §5.6）：列出应用版本
 * （版本号 / change_type / change_log / created_at），支持一键回滚到指定版本
 * （POST /debugpavilion/schemas/{id}/versions/{v}/restore），回滚成功后回调
 * 父组件刷新 schema。
 */
import { History, Loader2, RotateCcw } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { formatVersionTime, toVersionHistoryItems, type VersionHistoryItem } from "@/lib/builder/version-history";
import { listSchemaVersions, restoreSchemaVersion, type DebugSchemaDetail } from "@/services/debugpavilion";

export interface VersionHistoryDialogProps {
  appId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 回滚成功后的回调（携带恢复后的应用详情，供父组件刷新 schema 与状态） */
  onRestored?: (detail: DebugSchemaDetail) => void;
}

export function VersionHistoryDialog({ appId, open, onOpenChange, onRestored }: VersionHistoryDialogProps) {
  const [items, setItems] = React.useState<VersionHistoryItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [restoring, setRestoring] = React.useState<number | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const loadVersions = React.useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    try {
      const versions = await listSchemaVersions(appId);
      setItems(toVersionHistoryItems(versions));
    } catch (err) {
      setError(`版本历史加载失败：${String(err)}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  React.useEffect(() => {
    if (open && appId) {
      setMessage(null);
      void loadVersions();
    }
  }, [open, appId, loadVersions]);

  const handleRestore = async (version: number) => {
    if (!appId) return;
    setRestoring(version);
    setError(null);
    setMessage(null);
    try {
      const detail = await restoreSchemaVersion(appId, version);
      onRestored?.(detail);
      setMessage(`已恢复到 v${version}，当前版本 v${detail.current_version ?? "—"}。`);
      await loadVersions();
    } catch (err) {
      setError(`回滚失败：${String(err)}`);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-600" />
            版本历史
          </DialogTitle>
          <DialogDescription>查看应用 schema 的版本记录，可回滚到任意历史版本（会追加新版本）。</DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1" data-testid="version-history-list">
          {loading && items.length === 0 && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              加载中...
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">暂无版本记录。</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {message && <p className="text-sm text-green-600">{message}</p>}

          {items.map((item) => (
            <div
              key={item.id}
              data-version={item.version}
              className="flex items-center gap-3 rounded-lg border bg-card p-3"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-500/10 text-sm font-semibold text-indigo-600">
                v{item.version}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">
                    {item.changeTypeLabel}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{formatVersionTime(item.createdAt)}</span>
                </div>
                <p className="mt-1 truncate text-sm text-foreground" title={item.changeLog ?? undefined}>
                  {item.changeLog || "—"}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 shrink-0"
                data-restore-version={item.version}
                disabled={restoring !== null}
                onClick={() => void handleRestore(item.version)}
              >
                {restoring === item.version ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                )}
                回滚
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
